import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ContactModel } from '../../../contact/models/contact.model';
import { AnimalOriginModel } from '../../models/animal-origin.model';
import {
  CARE_OPTIONS,
  CareCondition,
  NUTRITION_OPTIONS,
  NutritionCondition,
  TEMPERAMENT_OPTIONS,
  TemperamentCondition,
} from '../../models/body-condition-score.model';

@Component({
  selector: 'app-secured-origin-details',
  templateUrl: './secured-origin-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SecuredOriginDetailsComponent {
  @Input({ required: true }) origin!: AnimalOriginModel;

  constructor(private readonly _t: TranslocoService) {}

  get responsibleContact(): ContactModel | null {
    return this.origin.handoverResponsibleContact ?? null;
  }

  get responsibleContactName(): string {
    const contact = this.responsibleContact;
    if (!contact) return '';
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ')
      || contact.companyName
      || `#${contact.id}`;
  }

  get responsibleContactPhones(): string {
    const contact = this.responsibleContact;
    if (!contact) return '';
    const phoneNumbers = contact.phoneNumbers
      ?.map(entry => entry.number)
      .filter((number): number is string => !!number) ?? [];
    return Array.from(new Set([
      ...phoneNumbers,
      contact.phoneNumber,
      contact.mobilePhoneNumber,
      contact.mobilePhoneNumber2,
    ].filter((number): number is string => !!number))).join(', ');
  }

  get reasonLabels(): string {
    return (this.origin.securingReasons ?? [])
      .map(reason => this._t.translate(`animalAdd.securing.reasons.options.${reason}`))
      .join(', ');
  }

  get healthStatusLabels(): string {
    return (this.origin.handoverHealthStatus ?? [])
      .map(status => this._t.translate(`animalAdd.securing.health.statusOptions.${status}`))
      .join(', ');
  }

  get careLabels(): string {
    return (this.origin.careCondition ?? []).map(value => this.careLabel(value)).join(', ');
  }

  get nutritionValue(): string {
    return this.origin.bcsNutrition ? this.nutritionLabel(this.origin.bcsNutrition) : '';
  }

  get temperamentLabels(): string {
    return (this.origin.temperament ?? []).map(value => this.temperamentLabel(value)).join(', ');
  }

  biteVictimTypeLabel(type: string): string {
    return this._t.translate(`animalAdd.securing.biteVictim.typeOptions.${type}`);
  }

  biteVictimGenderLabel(gender: string): string {
    return this._t.translate(`animalAdd.securing.biteVictim.genderOptions.${gender}`);
  }

  booleanLabel(value: boolean | null | undefined): string {
    return value == null ? '' : this._t.translate(value ? 'yes' : 'no');
  }

  careLabel(value: CareCondition): string {
    return this.translateOption(CARE_OPTIONS, value);
  }

  nutritionLabel(value: NutritionCondition): string {
    return this.translateOption(NUTRITION_OPTIONS, value);
  }

  temperamentLabel(value: TemperamentCondition): string {
    return this.translateOption(TEMPERAMENT_OPTIONS, value);
  }

  private translateOption(options: ReadonlyArray<{ value: string; label: string }>, value: string): string {
    const key = options.find(option => option.value === value)?.label;
    return key ? this._t.translate(key) : value;
  }
}
